import { z } from "zod"

import { seasonSchema } from "./season"

// Definition of the Interval schema
export const timeOfUseIntervalSchema = z.object({
  /** The unique ID of the time of use this Interval applies to */
  touId: z.number().int(),
  /** The name of the time of use this interval applies to */
  touName: z.string(),
  /** The time of use group ID this interval applies to */
  touGroupId: z.number().int(),
  /** The day and time this interval starts, in ISO 8601 format */
  fromDateTime: z.string(),
  /** The day and time this Interval ends, in ISO 8601 format */
  toDateTime: z.string(),
})

// Definition of the Period schema
export const timeOfUsePeriodSchema = z.object({
  /** The unique Genability ID of this period */
  touPeriodId: z.number().int(),
  /** The ID of the parent time of use this period belongs to */
  touId: z.number().int(),
  /** The day of the week this period starts (0-6: Monday-Sunday) */
  fromDayOfWeek: z.number().int(),
  /** The hour this period starts (0-23) */
  fromHour: z.number().int().nullable(),
  /** The minute this period starts (0-59) */
  fromMinute: z.number().int().nullable(),
  /** The day of the week this period ends (0-6: Monday-Sunday) */
  toDayOfWeek: z.number().int(),
  /** The hour this period ends (0-23) */
  toHour: z.number().int().nullable(),
  /** The minute this period ends (0-59) */
  toMinute: z.number().int().nullable(),
})

// Definition of the TimeOfUse schema
export const timeOfUseSchema = z.object({
  /** Unique Genability ID for each time of use */
  touId: z.number().int(),
  /** Associates the rate with a tariff (foreign key) */
  touGroupId: z.number().int(),
  /** ID of load serving entity this time of use group belongs to */
  lseId: z.number().int(),
  /** Display name of this TOU */
  touName: z.string(),
  /** The ID of the calendar of events and
   * holidays that this TOU should apply to */
  calendarId: z.number().int().nullable(),
  /** The Season object that this time of use applies to (if applicable) */
  season: seasonSchema.nullable(),
  /** Indicates if the timeOfUse includes a
   * calendar whose dates change from year-to-year */
  isDynamic: z.boolean(),
  /** The periods that comprise this time of use */
  touPeriods: z.array(timeOfUsePeriodSchema),
  /** Indicates whether this TimeOfUse is PUBLIC or PRIVATE */
  privacy: z.enum([`PUBLIC`, `PRIVATE`]).nullable().optional(),
})

// Definition of the TimeOfUseGroup schema
export const timeOfUseGroupSchema = z.object({
  /** ID of load serving entity this time of use group belongs to */
  lseId: z.number().int(),
  /** Genability ID for this time of use group */
  touGroupId: z.number().int(),
  /** The times of use that comprise this time of use group */
  timeOfUses: z.array(timeOfUseSchema),
})

export type TimeOfUseInterval = z.infer<typeof timeOfUseIntervalSchema>
export type TimeOfUsePeriod = z.infer<typeof timeOfUsePeriodSchema>
export type TimeOfUse = z.infer<typeof timeOfUseSchema>
export type TimeOfUseGroup = z.infer<typeof timeOfUseGroupSchema>
